/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Notifications component
 *
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.24.0
 */

import { FC, memo, useCallback, useState } from 'react';

import { Notifications, Typography } from '@nexign/scc-ui-kit';
import { useSelector } from 'react-redux';

import { ImgStyled } from '@/main/components/common/Block/Img';
import { makeSelectUserNotificationsTranslation } from '@modules/internationalization/intSelectors';
import { useNotificationsData } from '@modules/userNotifications/hooks/useNotificationsData';
import { icons as iconTheme } from '@theme/index';

import { useFooterParams } from '../hooks/useFooterParams';
import { useHeaderParams } from '../hooks/useHeaderParams';
import { useItemTooltipHints } from '../hooks/useItemTooltipHints';
import { makeSelectUserNotificationsItems, makeSelectUserNotificationsNewsEnabled } from '../selectors';
import { StyledNotificationsEmptyListContainer } from '../styled/UserNotificationsStyled';

export interface INotificationsComponentProps {
    handleMenuClose: () => void;
}

const NotificationsComponent: FC<INotificationsComponentProps> = ({ handleMenuClose }) => {
    const notificationTranslation = useSelector(makeSelectUserNotificationsTranslation());

    const notifications = useSelector(makeSelectUserNotificationsItems());
    const newsIsEnabled = useSelector(makeSelectUserNotificationsNewsEnabled());

    const [disableControls, setDisableControls] = useState(false);

    const [notificationsItems, { itemIsLoading }] = useNotificationsData(
        notifications,
        disableControls,
        handleMenuClose,
    );

    const isHeaderMenuDisabled = !notificationsItems || !notificationsItems.length;

    const handleDisableControls = useCallback((disable: boolean) => {
        setDisableControls(disable);
    }, []);

    const footerParams = useFooterParams(handleMenuClose);

    const headerParams = useHeaderParams(isHeaderMenuDisabled, handleDisableControls, handleMenuClose);

    const itemTooltipHints = useItemTooltipHints();

    const EmptyListComponent = (
        <StyledNotificationsEmptyListContainer>
            {!!iconTheme.noDataImage && <ImgStyled height="144px" src={iconTheme.noDataImage} width="144px" />}
            <Typography variant="h3">{notificationTranslation.emptyList}</Typography>
        </StyledNotificationsEmptyListContainer>
    );

    return (
        <Notifications
            withHeader
            disableItemHints={itemIsLoading}
            emptyList={EmptyListComponent}
            footerProps={footerParams}
            headerProps={headerParams}
            items={notificationsItems}
            tooltipItemHints={itemTooltipHints}
            withFooter={newsIsEnabled}
        />
    );
};

export const NotificationsComponentMemo = memo(NotificationsComponent);
